import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Pagination,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  BookmarkBorder as VocabIcon,
  QuestionAnswer as QuestionIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  useGetVocabularyWordsQuery,
  useDeleteVocabularyWordMutation,
} from "@/store/api/admin/exerciseApi";
import WordFormDialog from "./WordFormDialog";
import QuestionsDialog from "./QuestionsDialog";
import { WordItem } from "./WordItem";

const VocabularyManagement = ({ exerciseId, dayId, open, onClose }) => {
  const [wordDialogOpen, setWordDialogOpen] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const [questionsDialogOpen, setQuestionsDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 9;

  // API hooks for vocabulary words
  const {
    data: wordsResponse,
    isLoading: isWordsLoading,
    error: wordsError,
  } = useGetVocabularyWordsQuery(
    { exerciseId, dayId, page, limit },
    { skip: !exerciseId || !dayId }
  );

  const [deleteWord, { isLoading: isDeletingWord }] =
    useDeleteVocabularyWordMutation();

  const vocabularyWords = wordsResponse?.data?.vocabularyItems || [];
  const totalPages = wordsResponse?.pagination?.totalPages || 1;
  const totalCount = wordsResponse?.pagination?.total || 0;
  const isAnyLoading = isWordsLoading || isDeletingWord;

  // Word Management
  const handleOpenWordDialog = (word = null) => {
    setEditingWord(word);
    setWordDialogOpen(true);
  };

  const handleCloseWordDialog = () => {
    setWordDialogOpen(false);
    setEditingWord(null);
  };

  const handleDeleteWord = async (wordId) => {
    if (!window.confirm("Are you sure you want to delete this word?")) return;

    try {
      await deleteWord({ dayId, wordId }).unwrap();
      toast.success("Word deleted successfully");
    } catch (error) {
      console.error("Failed to delete word:", error);
      toast.error(error?.data?.message || "Failed to delete word");
    }
  };

  const handleManageQuestions = () => {
    setQuestionsDialogOpen(true);
  };

  const sortedWords = [...vocabularyWords].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        {isAnyLoading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress
              variant="indeterminate"
              color="primary"
              sx={{ height: 5 }}
            />
          </Box>
        )}
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <VocabIcon />
              <Typography variant="h5">Vocabulary Management</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Loading State */}

          {/* Error State */}
          {wordsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load vocabulary:{" "}
              {wordsError?.data?.message || "Unknown error"}
            </Alert>
          )}

          {/* Section Actions */}
          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenWordDialog()}
                disabled={isAnyLoading}
              >
                Add Word
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<QuestionIcon />}
                onClick={handleManageQuestions}
                disabled={isAnyLoading}
              >
                Manage Questions
              </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Words List */}
            <Typography variant="h6" gutterBottom>
              Vocabulary Words
            </Typography>

            {sortedWords.length > 0 ? (
              <>
                <Grid container spacing={2}>
                  {sortedWords.map((word) => (
                    <WordItem
                      key={word._id}
                      word={word}
                      handleOpenWordDialog={handleOpenWordDialog}
                      handleDeleteWord={handleDeleteWord}
                      isAnyLoading={isAnyLoading}
                    />
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mt={3}
                  >
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      Total: {totalCount} words
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">
                No vocabulary words added yet. Click "Add Word" to create the
                first word.
              </Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Word Form Dialog */}
      <WordFormDialog
        open={wordDialogOpen}
        onClose={handleCloseWordDialog}
        editingWord={editingWord}
        // onSubmit={handleSubmitWord}
        isLoading={isAnyLoading}
        dayId={dayId}
      />

      {/* Questions Dialog */}
      <QuestionsDialog
        open={questionsDialogOpen}
        onClose={() => setQuestionsDialogOpen(false)}
        dayId={dayId}
      />
    </>
  );
};

export default VocabularyManagement;
